// React
import { useEffect, useState } from "react"

// Types
import type { SuccessFetch } from "types"
import type { ContextType } from "../../types"

// Utils
import { useApi } from "utils"

// Types
type Data = ContextType["data"]
interface Filter {
  file_name: string
  file_status: string
  page: number
  limit: number
}
interface Pagination {
  page: number
  pages: number
  size: number
  total: number
}

export function useList(): {
  data: Data[]
  filter: Filter
  handleDropdownChange: (
    field: "file_name" | "file_status",
    value: string
  ) => void
  handlePaginationChange: (
    field: "limit" | "page",
    value: number
  ) => void
  isLoading: boolean
  pagination: Pagination
  refetch: () => void
} {
  // Vars
  const defaultData: Data[] = []
  const defaultPagination: Pagination = {
    page: 0,
    pages: 0,
    size: 0,
    total: 0
  }

  // Hooks
  const api = useApi()
  const [data, setData] = useState<Data[]>(defaultData)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [pagination, setPagination] = useState<Pagination>(defaultPagination)
  const [filter, setFilter] = useState<Filter>({
    file_name: "All",
    file_status: "1",
    limit: 10,
    page: 1
  })

  useEffect(() => {
    refetch()
    
    return () => {
      setData(defaultData)
      setPagination(defaultPagination)
    }

    // eslint-disable-next-line
  }, [filter])

  // Functions
  const handleDropdownChange = (
    field: "file_name" | "file_status",
    value: string
  ): void => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }))
  }
  const handlePaginationChange = (
    field: "limit" | "page",
    value: number
  ): void => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }))
  }
  const refetch = (): void => {
    setLoading(true)

    api.get(
      "/import_coa/listfile",
      { params: filter }
    ).then((res: SuccessFetch<{
      payload: {
        listfile: Data[]
        pagination_data: Pagination
      }
    }>) => {
      // Vars
      const payload = res.data.payload

      setData(payload.listfile)
      setPagination(payload.pagination_data)
    }).catch(() => {
      setData(defaultData)
      setPagination(defaultPagination)
    }).finally(() => {
      setLoading(false)
    })
  }

  return { data, filter, isLoading, pagination, handleDropdownChange, handlePaginationChange, refetch }
}